import BrainSearchDrawerContent from "@/brain-search/internal/drawer/BrainSearchDrawerContent"
import {
  ContentUsageDrawerContext,
  ContentUsageDrawerParent,
} from "@/content-usage/drawer/ContentUsageDrawerContext"
import {
  CONTENT_DRAWER_HEADER_HEIGHT_MEMBER,
  InlineContentDrawerFormStore,
} from "@/content-usage/drawer/InlineContentDrawerTemplate"
import { useContentUsageDrawer } from "@/content-usage/drawer/useContentUsageDrawer"
import GenerateContentWithAIProvider, {
  useGenerateContentWithAI,
} from "@/content/ai/GenerateContentWithAIProvider"
import UnsavedChangesModalProvider, {
  useInitUnsavedChangesModalContext,
} from "@/core/context/UnsavedChangesModalProvider"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoDrawer } from "@disco-ui"
import useEffectOnUpdate from "@utils/hook/useEffectOnUpdate"
import lazyWithRetry from "@utils/lazy/lazyWithRetry"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

const ContentUsageSubmissionsDrawerContent = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "content-usage-submissions-drawer-content" */ "@/content-usage/drawer/ContentUsageSubmissionsDrawerContent"
    )
)
const ContentUsageScormCompletionsDrawerContent = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "content-usage-scorm-completions-drawer-content" */ "@/content-usage/drawer/sidebar/scorm-completions/ContentUsageScormCompletionsDrawerContent"
    )
)
const ContentUsageDrawerContent = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "content-usage-drawer-content" */ "@/content-usage/drawer/ContentUsageDrawerContent"
    )
)

function ContentUsageDrawer() {
  const drawer = useContentUsageDrawer()

  return (
    <GenerateContentWithAIProvider
      prompt={
        drawer.params.drawerAIPrompt
          ? decodeURIComponent(drawer.params.drawerAIPrompt)
          : undefined
      }
      referenceUrls={
        drawer.params.drawerAIReferenceUrls
          ? JSON.parse(decodeURIComponent(drawer.params.drawerAIReferenceUrls))
          : undefined
      }
      referenceEmbeddingSourceIds={
        drawer.params.drawerAIReferenceEmbeddingSourceIds
          ? JSON.parse(
              decodeURIComponent(drawer.params.drawerAIReferenceEmbeddingSourceIds)
            )
          : undefined
      }
      referenceModuleContentUsageId={drawer.params.drawerAIReferenceModuleContentUsageId}
    >
      <Modal />
    </GenerateContentWithAIProvider>
  )
}

function Modal() {
  const [contentUsageForm, setContentUsageForm] = useState<InlineContentDrawerFormStore>()
  const [drawerParentEntity, setDrawerParentEntity] = useState<
    ContentUsageDrawerParent | undefined
  >()
  const [headerHeight, setHeaderHeight] = useState(CONTENT_DRAWER_HEADER_HEIGHT_MEMBER)
  const drawer = useContentUsageDrawer()
  const classes = useStyles()
  const location = useLocation()
  const modal = useInitUnsavedChangesModalContext()
  const { stopGenerating } = useGenerateContentWithAI()

  const isCurriculum = drawerParentEntity?.module?.parentEntity === "curriculum"
  const isCollection = drawerParentEntity?.module?.parentEntity === "collection"
  const isPageApp = drawerParentEntity?.productApp?.kind === "content"
  const isContentDashboardBlock = Boolean(drawerParentEntity?.contentDashboardBlock)
  const isShareLink = Boolean(drawerParentEntity?.organization)
  const isValidContentDrawer =
    isCurriculum || isCollection || isPageApp || isContentDashboardBlock || isShareLink
  const isBrainSearchOpen = Boolean(drawer.params.drawerAsk)

  // Sidebar collapsed state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true)

  // Expand the sidebar by default if the initial tab is set, or this is a curriculum
  // content and brain search isn't open
  useEffect(() => {
    if (!drawer.params.drawerTab && (!isCurriculum || isBrainSearchOpen)) return
    setIsSidebarCollapsed(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurriculum, drawer.params.drawerTab])

  // Auto-collapse sidebar when brain search is opened
  useEffectOnUpdate(() => {
    if (isBrainSearchOpen) setIsSidebarCollapsed(true)
  }, [isBrainSearchOpen])

  // If the drawer is closed, clear the form and sidebar entity
  useEffect(() => {
    if (!drawer.isOpen) {
      setContentUsageForm(undefined)
      setDrawerParentEntity(undefined)
    }
  }, [drawer.isOpen])

  return (
    // If leaving with unsaved changes, display the unsaved changes modal
    <UnsavedChangesModalProvider {...modal}>
      <ContentUsageDrawerContext.Provider
        value={{
          contentUsageForm,
          setContentUsageForm,
          drawerParentEntity,
          setDrawerParentEntity,
          isSidebarCollapsed,
          setIsSidebarCollapsed,
          isCurriculum,
          isCollection,
          isContentDashboardBlock,
          isPageApp,
          isShareLink,
          isValidContentDrawer,
          headerHeight,
          setHeaderHeight,
        }}
      >
        <DiscoDrawer
          size={"xl"}
          open={drawer.isOpen}
          onClose={handleCloseDrawer}
          drawerAnchorKey={location.search}
          containerClasses={{ drawerContainer: classes.drawerContainer }}
          fullScreen
          sidePanel={isBrainSearchOpen ? <BrainSearchDrawerContent /> : undefined}
          forceFullScreen={isBrainSearchOpen}
        >
          {renderContent()}
        </DiscoDrawer>
      </ContentUsageDrawerContext.Provider>
    </UnsavedChangesModalProvider>
  )

  function renderContent() {
    if (drawer.params.drawerSubmissionsTab)
      return (
        <ContentUsageSubmissionsDrawerContent
          contentUsageId={drawer.params.drawerContentUsageId}
        />
      )
    if (drawer.params.drawerScormCompletionsTab)
      return (
        <ContentUsageScormCompletionsDrawerContent
          contentUsageId={drawer.params.drawerContentUsageId}
        />
      )

    return <ContentUsageDrawerContent />
  }

  function handleCloseDrawer() {
    // Check that there are no unsaved changes before closing
    modal.handleLeave({
      onLeave: () => {
        stopGenerating()
        drawer.close()
      },
    })
  }
}

const useStyles = makeUseStyles((theme) => ({
  drawerContainer: {
    padding: 0,
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      display: "block",
    },
  },
}))

export default observer(ContentUsageDrawer)
